import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Globe, Copy, ExternalLink, Link2Off, Plus, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import { post, toastError } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const STATE = { active: ["Aktiv", "text-emerald-700 dark:text-emerald-300"], expired: ["Utløpt", "text-amber-700 dark:text-amber-300"], revoked: ["Trukket tilbake", "text-muted-foreground"] };

export const shareUrl = (share) => `${window.location.origin}${share.path}`;

const copy = async (text, { silent = false } = {}) => {
  let ok = false;
  try {
    await navigator.clipboard.writeText(text);
    ok = true;
  } catch {
    // fallback for browsers/contexts without the async clipboard API
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try { ok = document.execCommand("copy"); } catch { ok = false; }
    document.body.removeChild(ta);
  }
  if (ok) toast.success("Lenke kopiert");
  else if (!silent) toast.error("Kunne ikke kopiere – merk lenken manuelt");
  return ok;
};

/**
 * Web-tilbud: share links for the public proposal (/tilbud/vis/:token). Each link is bound to one immutable
 * version; the customer can accept or request changes there. Creating/revoking requires quotes:write.
 */
export const WebQuoteSheet = ({ open, onOpenChange, quote, canWrite, onChanged }) => {
  const qc = useQueryClient();
  const shares = quote.shares || [];
  const active = shares.filter((s) => s.state === "active");
  const versions = quote.versions || [];
  const [form, setForm] = useState({ version_id: quote.current_version_id || "", expires_days: 30, message: "" });
  const [showNew, setShowNew] = useState(active.length === 0);
  const currentV = useMemo(() => versions.find((v) => v.id === quote.current_version_id), [versions, quote.current_version_id]);

  const create = useMutation({
    mutationFn: () => post(`/quotes/${quote.id}/share`, { version_id: form.version_id || undefined, expires_days: form.expires_days ? Number(form.expires_days) : undefined, message: form.message || undefined }),
    onSuccess: async (s) => {
      toast.success(`Web-tilbud v${s.version} er klart`);
      await copy(shareUrl(s), { silent: true });
      setShowNew(false);
      qc.invalidateQueries({ queryKey: ["quote", quote.id] });
      onChanged?.();
    },
    onError: toastError,
  });
  const revoke = useMutation({
    mutationFn: (sid) => post(`/quote-shares/${sid}/revoke`, {}),
    onSuccess: () => { toast.success("Lenken er trukket tilbake"); qc.invalidateQueries({ queryKey: ["quote", quote.id] }); onChanged?.(); },
    onError: toastError,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-lg" data-testid="web-quote-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Globe className="h-4 w-4" /> Web-tilbud</SheetTitle>
          <SheetDescription>Kunden åpner tilbudet i nettleseren, kan laste ned PDF, akseptere eller be om endringer. Lenken er bundet til én versjon og kan trekkes tilbake når som helst.</SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-6">
          {active.length > 0 && (
            <section className="space-y-3" data-testid="web-quote-active">
              <h3 className="text-[12px] font-medium uppercase tracking-wide text-muted-foreground">Aktive lenker</h3>
              {active.map((s) => (
                <div key={s.id} className="rounded-lg border border-border/70 p-3" data-testid={`web-quote-share-${s.id}`}>
                  <div className="flex items-center justify-between gap-2 text-[13px]">
                    <span className="font-medium">Versjon {s.version}{s.version_id === quote.current_version_id ? <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">gjeldende</span> : null}</span>
                    <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground"><Eye className="h-3.5 w-3.5" /> {s.views || 0} {s.views === 1 ? "visning" : "visninger"}{s.last_viewed_at ? ` · sist ${fmtDateTime(s.last_viewed_at)}` : ""}</span>
                  </div>
                  <Input readOnly value={shareUrl(s)} onFocus={(e) => e.target.select()} className="mt-2 h-8 font-mono text-[11.5px]" data-testid="web-quote-link" />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button size="sm" className="h-8" onClick={() => window.open(shareUrl(s), "_blank", "noopener")} data-testid="web-quote-open-button"><ExternalLink /> Åpne</Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => copy(shareUrl(s))} data-testid="web-quote-copy-button"><Copy /> Kopier lenke</Button>
                    {canWrite && <Button size="sm" variant="ghost" className="h-8 text-muted-foreground hover:text-foreground" onClick={() => revoke.mutate(s.id)} disabled={revoke.isPending} data-testid="web-quote-revoke-button"><Link2Off /> Trekk tilbake</Button>}
                    <span className="ml-auto text-[11.5px] text-muted-foreground">{s.expires_at ? `Utløper ${fmtDate(s.expires_at)}` : "Utløper ikke"}</span>
                  </div>
                </div>
              ))}
            </section>
          )}

          {canWrite && (showNew || active.length === 0 ? (
            <section className="space-y-3 rounded-lg border border-dashed border-border p-3" data-testid="web-quote-new">
              <h3 className="text-[13px] font-medium">{active.length ? "Ny delingslenke" : "Del tilbudet som web-tilbud"}</h3>
              {!currentV?.lines?.length && <p className="text-[12.5px] text-amber-700 dark:text-amber-300">Gjeldende versjon har ingen prislinjer – legg inn og lagre linjer først.</p>}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[12px]">Versjon</Label>
                  <Select value={form.version_id} onValueChange={(v) => setForm((f) => ({ ...f, version_id: v }))}>
                    <SelectTrigger className="h-9" data-testid="web-quote-version-select"><SelectValue placeholder="Velg versjon" /></SelectTrigger>
                    <SelectContent>{versions.map((v) => <SelectItem key={v.id} value={v.id}>v{v.version} · {v.status}{v.id === quote.current_version_id ? " · gjeldende" : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[12px]">Gyldig i (dager)</Label>
                  <Input type="number" min={1} max={365} value={form.expires_days} onChange={(e) => setForm((f) => ({ ...f, expires_days: e.target.value }))} className="h-9" data-testid="web-quote-expires-input" />
                </div>
              </div>
              <div>
                <Label className="text-[12px]">Personlig melding <span className="font-normal text-muted-foreground">(valgfritt, vises øverst)</span></Label>
                <Textarea rows={3} maxLength={600} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder="Hei – her er tilbudet vi snakket om. Ta gjerne kontakt om noe er uklart." data-testid="web-quote-message-input" />
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => create.mutate()} disabled={create.isPending || !form.version_id} data-testid="web-quote-create-button">{create.isPending ? <Loader2 className="animate-spin" /> : <Globe />} Opprett lenke og kopier</Button>
                {active.length > 0 && <Button variant="ghost" onClick={() => setShowNew(false)}>Avbryt</Button>}
              </div>
            </section>
          ) : (
            <Button variant="outline" size="sm" className="h-8" onClick={() => setShowNew(true)} data-testid="web-quote-new-button"><Plus /> Ny delingslenke</Button>
          ))}

          {shares.some((s) => s.state !== "active") && (
            <section className="space-y-2" data-testid="web-quote-history">
              <h3 className="text-[12px] font-medium uppercase tracking-wide text-muted-foreground">Tidligere lenker</h3>
              <ul className="divide-y divide-border/60 text-[12.5px]">
                {shares.filter((s) => s.state !== "active").map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 py-2">
                    <span>v{s.version} · opprettet {fmtDate(s.created_at)}{s.created_by_name ? ` av ${s.created_by_name}` : ""}</span>
                    <span className={cn("shrink-0", STATE[s.state]?.[1])}>{STATE[s.state]?.[0] || s.state}{s.views ? ` · ${s.views} visn.` : ""}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
